import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, ArrowRight } from 'lucide-react';
import { FaSeedling, FaLeaf, FaTint, FaHandshake, FaCheckCircle, FaShoppingBag } from 'react-icons/fa';
import { MdLocalFlorist, MdSpa, MdYard, MdGrass } from 'react-icons/md';
import './ServiceDetail.css';

const ACCENT = '#558b2f';
const ACCENT_LIGHT = '#8bc34a';
const ACCENT_RGB = '85,139,47';

const benefits = [
  {
    emoji: <FaSeedling size={22} />,
    title: 'Quality Plant Care Products',
    desc: 'Carefully selected gardening products designed to support healthy and sustainable plant growth.'
  },
  {
    emoji: <MdLocalFlorist size={22} />,
    title: 'Wide Range Of Pots',
    desc: 'Decorative and functional plant pots suitable for homes, offices, balconies, and gardens.'
  },
  {
    emoji: <FaLeaf size={22} />,
    title: 'Organic Growing Solutions',
    desc: 'Natural fertilizers and plant care products that promote healthier plant development.'
  },
  {
    emoji: <FaTint size={22} />,
    title: 'Essential Gardening Tools',
    desc: 'Practical accessories and gardening supplies for everyday plant maintenance.'
  },
  {
    emoji: <MdSpa size={22} />,
    title: 'Suitable For All Plants',
    desc: 'Products suitable for indoor plants, outdoor plants, flowering plants, herbs, and fruit plants.'
  },
  {
    emoji: <FaHandshake size={22} />,
    title: 'Expert Product Guidance',
    desc: 'Get recommendations from experienced nursery professionals based on your plant needs.'
  },
];

const products = [
  {
    emoji: <MdLocalFlorist size={22} />,
    name: 'Decorative Plant Pots',
    desc: 'Stylish pots and planters suitable for indoor plants, balconies, gardens, and office spaces.'
  },
  {
    emoji: <FaSeedling size={22} />,
    name: 'Organic Fertilizers',
    desc: 'Plant nutrition products that support healthy growth, flowering, and root development.'
  },
  {
    emoji: <FaLeaf size={22} />,
    name: 'Potting Soil Mixes',
    desc: 'Quality soil blends designed for indoor plants, flowering plants, herbs, and succulents.'
  },
  {
    emoji: <FaTint size={22} />,
    name: 'Plant Care Accessories',
    desc: 'Essential gardening tools and accessories for daily plant care and maintenance.'
  },
  {
    emoji: <MdSpa size={22} />,
    name: 'Flowering Plant Nutrition',
    desc: 'Specialized plant care products to support blooming and seasonal plant growth.'
  },
  {
    emoji: <MdYard size={22} />,
    name: 'Garden Care Products',
    desc: 'Useful gardening essentials designed for home gardens, terraces, and landscapes.'
  },
];

const stats = [
  { val: <MdLocalFlorist size={28} />, label: 'Plant Pots' },
  { val: <FaSeedling size={28} />, label: 'Organic Fertilizers' },
  { val: <FaLeaf size={28} />, label: 'Plant Care Products' },
  { val: <FaTint size={28} />, label: 'Gardening Essentials' },
];

export default function PlantStorePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Plant Food, Pots & Accessories — Nisarg Nursery';
    const observer = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.12 }
    );
    document.querySelectorAll('.sdp-observe').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="sdp-wrapper"
      style={{ '--accent': ACCENT, '--accent-light': ACCENT_LIGHT, '--accent-dark': '#33691e', '--accent-rgb': ACCENT_RGB }}
    >
      {/* ════ HERO ════ */}
      <section className="sdp-hero">
        <div className="sdp-hero-bg">
          <img src="/images/palm.png" alt="Plant Food & Pots" className="sdp-hero-img" />
          <div className="sdp-hero-overlay" />
        </div>
        <div className="sdp-hero-content">
          <nav className="sdp-breadcrumb">
            <Link to="/">Home</Link> › <Link to="/services">Services</Link> › <span>Plant Food & Pots</span>
          </nav>
          <div className="sdp-service-tag">
            <ShoppingBag size={16} /> <FaLeaf /> Plant Care Essentials
          </div>
          <span className="sdp-hero-icon"><ShoppingBag size={56} strokeWidth={1.5} /></span>
          <h1 className="sdp-hero-title">
            Premium Plant Pots, <span className="accent-word">Fertilizers & Garden Accessories</span>
          </h1>
          <p className="sdp-hero-desc">
            Discover quality plant pots, organic fertilizers, gardening accessories, soil mixes, and plant care essentials designed to support healthy indoor and outdoor plant growth.
          </p>
          <div className="sdp-hero-cta">
            <Link to="/contact" className="sdp-btn-primary">
              Shop Now <ArrowRight size={16} />
            </Link>
            <Link to="/services" className="sdp-btn-outline">← Back to Services</Link>
          </div>
        </div>
        <div className="sdp-hero-scroll">
          <span>SCROLL</span>
          <div className="sdp-scroll-line" />
        </div>
      </section>

      {/* ── STATS ── */}
      <div className="sdp-stats">
        {stats.map(s => (
          <div key={s.label} className="sdp-stat sdp-observe">
            <span className="sdp-stat-val">{s.val}</span>
            <span className="sdp-stat-label">{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── OVERVIEW ── */}
      <section className="sdp-section sdp-overview">
        <div className="sdp-section-inner">
          <div className="sdp-overview-grid">
            <div className="sdp-overview-left sdp-observe">
              <div className="sdp-overview-img-wrap">
                <img src="/images/Peace Lily.jpg" alt="Plant Products" className="sdp-overview-img" />
                <div className="sdp-overview-badge">
                  <div className="sdp-overview-badge-dot" />
                  80+ Products In Stock
                </div>
              </div>
            </div>
            <div className="sdp-overview-right sdp-observe">
              <div className="sdp-section-tag"><FaLeaf /> Complete Plant Care Solutions</div>
              <h2 className="sdp-section-title">Everything Your Plants Need <br />For Healthy Growth</h2>
              <div className="sdp-overview-text">
                <p>Nisarg Nursery offers a carefully selected range of plant pots, organic fertilizers, soil mixes, gardening accessories, and plant care products for homes, offices, balconies, and gardens.</p>
                <p>Whether you are growing indoor plants, flowering plants, herbs, or outdoor landscaping plants, our quality gardening products help support stronger roots, healthier growth, and long-lasting plant health.</p>
              </div>
              <div className="sdp-features-list">
                {[
                  'Plant Pots',
                  'Organic Fertilizers',
                  'Soil Mixes',
                  'Garden Accessories',
                  'Plant Nutrition',
                  'Plant Care Solutions'
                ].map(f => (
                  <div key={f} className="sdp-feature-item">
                    <div className="sdp-feature-icon"><FaCheckCircle /></div>
                    {f}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRODUCT HIGHLIGHTS ── */}
      <section className="sdp-section sdp-observe">
        <div className="sdp-section-inner">
          <div className="sdp-section-tag"><FaShoppingBag /> Plant Care Collection</div>
          <h2 className="sdp-section-title">Popular Plant Care,<br />Products & Accessories</h2>
          <div className="sdp-benefits-grid">
            {products.map(p => (
              <div key={p.name} className="sdp-benefit-card sdp-observe">
                <span className="sdp-benefit-emoji">{p.emoji}</span>
                <h3 className="sdp-benefit-title">{p.name}</h3>
                <p className="sdp-benefit-desc">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section className="sdp-section sdp-observe">
        <div className="sdp-section-inner">
          <div className="sdp-section-tag"><FaCheckCircle /> Why Choose Nisarg Nursery</div>
          <h2 className="sdp-section-title">Trusted Plant Care<br />Products For Every Garden</h2>
          <div className="sdp-benefits-grid">
            {benefits.map(b => (
              <div key={b.title} className="sdp-benefit-card sdp-observe">
                <span className="sdp-benefit-emoji">{b.emoji}</span>
                <h3 className="sdp-benefit-title">{b.title}</h3>
                <p className="sdp-benefit-desc">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <div className="sdp-cta sdp-observe">
        <span className="sdp-cta-icon"><FaSeedling size={28} /></span>
        <h2 className="sdp-cta-title">Give Your Plants The Care They Deserve</h2>
        <p className="sdp-cta-desc">Contact Nisarg Nursery for quality plant pots, organic fertilizers, soil mixes, gardening accessories, and expert plant care recommendations for your home or garden.</p>
        <div className="sdp-cta-btns">
          <Link to="/contact" className="sdp-btn-primary">
            Contact Us
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
          </Link>
          <Link to="/services" className="sdp-btn-outline">← All Services</Link>
        </div>
      </div>
    </div>
  );
}
